@if (contact$ | async; as contact) {
  <div class="contact-overview-page min-h-full w-full">
    <app-header
      [title]="'TITLES.CONTACT' | translate"
      [subtitle]="(contactIdentifier$ | async) || ('COMPONENTS.CONTACT_OVERVIEW.SUBTITLE' | translate)"
    >
      <div header-actions class="hidden lg:block">
        <app-navbar-profile-info></app-navbar-profile-info>
      </div>
    </app-header>

    <div class="relative z-10 mx-auto max-w-6xl px-4 py-6 md:px-8" [formGroup]="contactForm.form">
      @if (isLoading) {
        <div class="contact-overview-page__loading">
          <div class="contact-overview-page__spinner" aria-hidden="true"></div>
          <span class="sr-only">{{ "MESSAGES.LOADING" | translate }}</span>
        </div>
      }

      <div class="mb-3">
        <app-back-button [fallbackUrl]="contactsBackUrl" />
      </div>

      <section class="contact-overview-page__card mb-4 p-6">
        <div class="flex flex-col items-start gap-6 md:flex-row md:items-center">
          <div class="contact-overview-avatar-shell">
            @if ((avatarUrl$ | async) !== notFound) {
              <div class="contact-overview-avatar">
                <img [src]="avatarUrl$ | async" alt="" />
              </div>
            } @else {
              <div
                class="contact-overview-avatar flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-2xl font-bold text-white shadow-lg"
                appNoImage
                height="80px"
                width="80px"
                maxWidth="80px"
                borderRadius="999px"
                fontSize="28px"
              >
                {{ contact.fullName | abbreviation }}
              </div>
            }

            @if (canEditContact$ | async) {
              <app-file-upload
                id="avatar"
                [fileExtensions]="imageExtensions"
                [prevExtension]="prevImageExtension"
                [path]="contactImagePath"
                [fileTitle]="'avatar'"
                (uploadedFileEvent)="onUploadFile($event)"
                (deletedFileEvent)="onDeleteFile()"
              >
                <ng-template #customTemplate let-fileInputId="fileInputId">
                  <label [attr.for]="fileInputId" class="contact-overview-avatar__upload">
                    <span aria-hidden="true">+</span>
                  </label>
                </ng-template>
              </app-file-upload>
            }
          </div>

          <div class="min-w-0 flex-1">
            <div class="mb-1 flex flex-wrap items-center gap-2">
              <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                {{ contact.fullName }}
              </h1>

              @if (contact.number) {
                <span class="font-mono text-sm text-neutral-400 dark:text-neutral-500">
                  {{ contact.number }}
                </span>
              }
            </div>

            <p class="mb-3 text-sm text-neutral-500 dark:text-neutral-400">
              @if (contactPosition$ | async; as position) {
                <span>{{ position }}</span>
              }
              @if ((contactPosition$ | async) && contact.organizationTitle) {
                <span> · </span>
              }
              @if (contact.organizationTitle) {
                <span>{{ contact.organizationTitle }}</span>
              }
              @if (contact.responsibleUserName) {
                <span> · {{ "INPUTS.RESPONSIBLE" | translate }}: {{ contact.responsibleUserName }}</span>
              }
            </p>

            <div class="flex flex-wrap gap-1.5">
              @if (contact.isCustomer) {
                <span class="contact-overview-page__type-badge contact-overview-page__type-badge--customer">
                  {{ "TOOLTIPS.ENTITY_TYPE_CUSTOMER" | translate }}
                </span>
              }
              @if (contact.isVendor) {
                <span class="contact-overview-page__type-badge contact-overview-page__type-badge--vendor">
                  {{ "TOOLTIPS.ENTITY_TYPE_VENDOR" | translate }}
                </span>
              }
              @if (!contact.isCustomer && !contact.isVendor) {
                <span class="contact-overview-page__type-badge contact-overview-page__type-badge--regular">
                  {{ "TOOLTIPS.ENTITY_TYPE_REGULAR" | translate }}
                </span>
              }
            </div>
          </div>

          <div class="flex shrink-0 flex-wrap items-center gap-2">
            @if (contact.phoneNumber) {
              <a
                [href]="'tel:' + contact.phoneNumber"
                class="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                <span aria-hidden="true" class="inline-flex h-3.5 w-3.5 items-center justify-center">
                  <svg viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.4386 11.0001C18.2186 11.0001 17.9886 10.9301 17.7686 10.8801C17.3231 10.7819 16.8853 10.6516 16.4586 10.4901C15.9947 10.3213 15.4848 10.3301 15.0269 10.5147C14.5691 10.6993 14.1957 11.0467 13.9786 11.4901L13.7586 11.9401C12.7846 11.3983 11.8896 10.7253 11.0986 9.94011C10.3134 9.14913 9.64046 8.25411 9.09864 7.28011L9.51864 7.00011C9.962 6.78303 10.3094 6.40964 10.494 5.9518C10.6786 5.49396 10.6874 4.98402 10.5186 4.52011C10.3599 4.09253 10.2296 3.65491 10.1286 3.21011C10.0786 2.99011 10.0386 2.76011 10.0086 2.53011C9.8872 1.82573 9.51826 1.18785 8.96826 0.731352C8.41825 0.274849 7.72333 0.0297211 7.00864 0.0401122H4.00864C3.57767 0.0360657 3.15089 0.124926 2.75735 0.300643C2.36381 0.47636 2.01275 0.734808 1.72807 1.05839C1.44339 1.38198 1.23177 1.76311 1.10762 2.17583C0.983477 2.58855 0.949715 3.02317 1.00864 3.45011C1.54137 7.63949 3.45466 11.532 6.44629 14.5127C9.43791 17.4935 13.3373 19.3926 17.5286 19.9101H17.9086C18.6461 19.9112 19.358 19.6406 19.9086 19.1501C20.225 18.8672 20.4777 18.5203 20.6501 18.1324C20.8225 17.7446 20.9106 17.3245 20.9086 16.9001V13.9001C20.8964 13.2055 20.6434 12.5367 20.193 12.0078C19.7425 11.4789 19.1225 11.1227 18.4386 11.0001Z" stroke="currentColor" stroke-width="1.5" />
                  </svg>
                </span>
                {{ "BUTTONS.CALL" | translate }}
              </a>
            }

            @if (contact.email) {
              <a
                [href]="'mailto:' + contact.email"
                class="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                <span aria-hidden="true" class="inline-flex h-3.5 w-3.5 items-center justify-center">
                  <svg viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 0H3C1.34315 0 0 1.34315 0 3V13C0 14.6569 1.34315 16 3 16H17C18.6569 16 20 14.6569 20 13V3C20 1.34315 18.6569 0 17 0ZM16.59 2L10.71 7.88C10.5225 8.06625 10.2689 8.17072 10.0045 8.17072C9.74013 8.17072 9.48651 8.06625 9.299 7.88L3.41 2H16.59ZM18 13C18 13.2652 17.8946 13.5196 17.7071 13.7071C17.5196 13.8946 17.2652 14 17 14H3C2.73478 14 2.48043 13.8946 2.29289 13.7071C2.10536 13.5196 2 13.2652 2 13V3.41L7.88 9.29C8.4425 9.8518 9.205 10.1674 10 10.1674C10.795 10.1674 11.5575 9.8518 12.12 9.29L18 3.41V13Z" fill="currentColor" />
                  </svg>
                </span>
                {{ "INPUTS.EMAIL" | translate }}
              </a>
            }

            @if (canDeleteContact$ | async) {
              <button
                type="button"
                class="contact-overview-page__delete-button form-secondary-btn form-secondary-btn--small"
                (click)="deleteContact()"
              >
                <span aria-hidden="true" class="inline-flex h-3.5 w-3.5 items-center justify-center">
                  <svg viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 16C7.26522 16 7.51957 15.8946 7.70711 15.7071C7.89464 15.5196 8 15.2652 8 15V9C8 8.73478 7.89464 8.48043 7.70711 8.29289C7.51957 8.10536 7.26522 8 7 8C6.73478 8 6.48043 8.10536 6.29289 8.29289C6.10536 8.48043 6 8.73478 6 9V15C6 15.2652 6.10536 15.5196 6.29289 15.7071C6.48043 15.8946 6.73478 16 7 16ZM17 4H13V3C13 2.20435 12.6839 1.44129 12.1213 0.87868C11.5587 0.316071 10.7956 0 10 0H8C7.20435 0 6.44129 0.316071 5.87868 0.87868C5.31607 1.44129 5 2.20435 5 3V4H1C0.734784 4 0.48043 4.10536 0.292893 4.29289C0.105357 4.48043 0 4.73478 0 5C0 5.26522 0.105357 5.51957 0.292893 5.70711C0.48043 5.89464 0.734784 6 1 6H2V17C2 17.7956 2.31607 18.5587 2.87868 19.1213C3.44129 19.6839 4.20435 20 5 20H13C13.7956 20 14.5587 19.6839 15.1213 19.1213C15.6839 18.5587 16 17.7956 16 17V6H17C17.2652 6 17.5196 5.89464 17.7071 5.70711C17.8946 5.51957 18 5.26522 18 5C18 4.73478 17.8946 4.48043 17.7071 4.29289C17.5196 4.10536 17.2652 4 17 4Z" fill="currentColor" />
                  </svg>
                </span>
                {{ "BUTTONS.DELETE_CONTACT" | translate }}
              </button>
            }
          </div>
        </div>
      </section>

      <div class="contact-overview-page__tabs sticky top-2 z-20 mb-4">
        <app-tabs
          [items]="(tabItems$ | async) ?? []"
          [activeId]="(activeTab$ | async) ?? 'overview'"
          customClass="contact-overview-tabs"
          (activeIdChange)="selectTab($event)"
        />
      </div>

      @if (activeTab$ | async; as activeTab) {
        @switch (activeTab) {
          @case ("overview") {
            <section class="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div class="space-y-6 lg:col-span-2">
                <div class="contact-overview-page__card p-6">
                  <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.SECTION_TITLES.PERSONAL" | translate }}
                  </h2>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.surname"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.surname)"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.SURNAME" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ contact.surname || "—" }}</p>
                      </ng-template>
                    </app-dynamic-editable-field>

                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.name"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.name)"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.NAME" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ contact.name || "—" }}</p>
                      </ng-template>
                    </app-dynamic-editable-field>

                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.patronymic"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.patronymic)"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.PATRONYMIC" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ contact.patronymic || "—" }}</p>
                      </ng-template>
                    </app-dynamic-editable-field>
                  </div>

                  <div class="mt-4 grid grid-cols-1 gap-4 border-t border-neutral-100 pt-4 dark:border-neutral-800 sm:grid-cols-2">
                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.position"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.position)"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.POSITION" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ (contactPosition$ | async) || "—" }}</p>
                      </ng-template>
                    </app-dynamic-editable-field>

                    <app-dynamic-editable-field
                      inputType="custom"
                      layout="stacked"
                      editorClass="w-full"
                      [formControlName]="contactForm.fieldNames.contactType"
                      (updatedInput)="updateContactType()"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">
                          {{ "COMPONENTS.CONTACT_OVERVIEW.INFORMATION.TYPE" | translate }}
                        </span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <div class="mt-0.5 flex gap-1.5">
                          @if (contact.isCustomer) {
                            <span class="contact-overview-page__type-badge contact-overview-page__type-badge--customer">
                              {{ "TOOLTIPS.ENTITY_TYPE_CUSTOMER" | translate }}
                            </span>
                          }
                          @if (contact.isVendor) {
                            <span class="contact-overview-page__type-badge contact-overview-page__type-badge--vendor">
                              {{ "TOOLTIPS.ENTITY_TYPE_VENDOR" | translate }}
                            </span>
                          }
                          @if (!contact.isCustomer && !contact.isVendor) {
                            <span class="contact-overview-page__type-badge contact-overview-page__type-badge--regular">
                              {{ "TOOLTIPS.ENTITY_TYPE_REGULAR" | translate }}
                            </span>
                          }
                        </div>
                      </ng-template>
                      <ng-template #inputTpl let-value="value" let-update="update">
                        <div class="contact-overview-page__type-editor">
                          <label class="contact-overview-page__type-option">
                            <input
                              type="radio"
                              name="contactType"
                              [checked]="value?.isCustomer"
                              (change)="update({ isCustomer: true, isVendor: false })"
                            />
                            <span>{{ "TOOLTIPS.ENTITY_TYPE_CUSTOMER" | translate }}</span>
                          </label>
                          <label class="contact-overview-page__type-option">
                            <input
                              type="radio"
                              name="contactType"
                              [checked]="value?.isVendor"
                              (change)="update({ isCustomer: false, isVendor: true })"
                            />
                            <span>{{ "TOOLTIPS.ENTITY_TYPE_VENDOR" | translate }}</span>
                          </label>
                          <label class="contact-overview-page__type-option">
                            <input
                              type="radio"
                              name="contactType"
                              [checked]="!value?.isCustomer && !value?.isVendor"
                              (change)="update({ isCustomer: false, isVendor: false })"
                            />
                            <span>{{ "TOOLTIPS.ENTITY_TYPE_REGULAR" | translate }}</span>
                          </label>
                        </div>
                      </ng-template>
                    </app-dynamic-editable-field>

                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.birthDate"
                      inputType="date"
                      inputClass="input-sm"
                      layout="stacked"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.birthDate)"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.BIRTH_DATE" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl>
                        <p class="text-sm text-neutral-700 dark:text-neutral-300">
                          {{ contact.birthDate ? (contact.birthDate | date: activityDateFormat) : ("COMMON.EMPTY_VALUE" | translate) }}
                        </p>
                      </ng-template>
                    </app-dynamic-editable-field>
                  </div>
                </div>

                <div class="contact-overview-page__card p-6">
                  <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.SECTION_TITLES.CONTACT" | translate }}
                  </h2>

                  <div class="space-y-3">
                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.phoneNumber"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      [showEditTrigger]="false"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.phoneNumber)"
                    >
                      <ng-template #labelTpl>
                        <span class="sr-only">{{ "INPUTS.PHONE" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl let-startEdit="startEdit" let-canEdit="canEdit">
                        <div class="flex items-center gap-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800">
                          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40">
                            <svg class="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                            </svg>
                          </div>
                          <div class="min-w-0 flex-1">
                            <p class="text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.PHONE" | translate }}</p>
                            <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ contact.phoneNumber || "—" }}</p>
                          </div>
                          <div class="hidden shrink-0 items-center gap-2 md:flex">
                            @if (canEdit) {
                              <button
                                type="button"
                                class="contact-overview-page__inline-edit default-transparent-btn"
                                (click)="startEdit()"
                                [ngbTooltip]="'BUTTONS.EDIT' | translate"
                              >
                                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M14.85 2.65L17.35 5.15L6.6 15.9H4.1V13.4L14.85 2.65ZM15.55 1.95L14.15 3.35L16.65 5.85L18.05 4.45C18.43 4.06 18.43 3.44 18.05 3.05L16.95 1.95C16.56 1.57 15.94 1.57 15.55 1.95Z" fill="currentColor"/>
                                </svg>
                              </button>
                            }
                            @if (contact.phoneNumber) {
                              <a [href]="'tel:' + contact.phoneNumber" class="text-xs text-blue-600 hover:underline dark:text-blue-400">
                                {{ "BUTTONS.CALL" | translate }}
                              </a>
                            }
                          </div>
                          @if (canEdit || contact.phoneNumber) {
                            <div class="flex shrink-0 md:hidden">
                              <app-action-menu
                                [items]="contact.phoneNumber ? (canEdit ? phoneActionMenuItemsWithEdit : phoneActionMenuItems) : editOnlyActionMenuItems"
                                [ariaLabelKey]="'BUTTONS.ACTIONS'"
                                (actionSelected)="onContactDataActionSelect($event, startEdit, contact.phoneNumber)"
                              />
                            </div>
                          }
                        </div>
                      </ng-template>
                    </app-dynamic-editable-field>

                    <app-dynamic-editable-field
                      [formControlName]="contactForm.fieldNames.email"
                      inputType="text"
                      inputClass="input-sm"
                      layout="stacked"
                      [showEditTrigger]="false"
                      (updatedInput)="onUpdateInput(contactForm.fieldNames.email)"
                    >
                      <ng-template #labelTpl>
                        <span class="sr-only">{{ "INPUTS.EMAIL" | translate }}</span>
                      </ng-template>
                      <ng-template #contentTpl let-startEdit="startEdit" let-canEdit="canEdit">
                        <div class="flex items-center gap-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800">
                          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
                            <svg class="h-4 w-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                          </div>
                          <div class="min-w-0 flex-1">
                            <p class="text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.EMAIL" | translate }}</p>
                            <p class="truncate text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ contact.email || "—" }}</p>
                          </div>
                          <div class="hidden shrink-0 items-center gap-2 md:flex">
                            @if (canEdit) {
                              <button
                                type="button"
                                class="contact-overview-page__inline-edit default-transparent-btn"
                                (click)="startEdit()"
                                [ngbTooltip]="'BUTTONS.EDIT' | translate"
                              >
                                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <path d="M14.85 2.65L17.35 5.15L6.6 15.9H4.1V13.4L14.85 2.65ZM15.55 1.95L14.15 3.35L16.65 5.85L18.05 4.45C18.43 4.06 18.43 3.44 18.05 3.05L16.95 1.95C16.56 1.57 15.94 1.57 15.55 1.95Z" fill="currentColor"/>
                                </svg>
                              </button>
                            }
                            @if (contact.email) {
                              <a [href]="'mailto:' + contact.email" class="text-xs text-blue-600 hover:underline dark:text-blue-400">
                                {{ "BUTTONS.WRITE" | translate }}
                              </a>
                            }
                          </div>
                          @if (canEdit || contact.email) {
                            <div class="flex shrink-0 md:hidden">
                              <app-action-menu
                                [items]="contact.email ? (canEdit ? emailActionMenuItemsWithEdit : emailActionMenuItems) : editOnlyActionMenuItems"
                                [ariaLabelKey]="'BUTTONS.ACTIONS'"
                                (actionSelected)="onContactDataActionSelect($event, startEdit, contact.email)"
                              />
                            </div>
                          }
                        </div>
                      </ng-template>
                    </app-dynamic-editable-field>
                  </div>
                </div>

                <div class="contact-overview-page__card p-6">
                  <app-contact-delivery-addresses-section
                    [contactId]="contact.id"
                    [deliveryAddresses]="contact.deliveryAddresses"
                    [editable]="(canEditContact$ | async) ?? false"
                    (deliveryAddressesChange)="onDeliveryAddressesChanged($event)"
                  />
                </div>
              </div>

              <div class="space-y-6">
                <div class="contact-overview-page__card p-6">
                  <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.SECTION_TITLES.RESPONSIBILITY" | translate }}
                  </h2>

                  <div class="space-y-4">
                    <app-dynamic-editable-field
                      inputType="custom"
                      class="block w-full"
                      [showEditButtonOnMobile]="true"
                      editorClass="w-full"
                      [formControlName]="contactForm.fieldNames.organizationId"
                      (updatedInput)="onUpdateOrganization()"
                    >
                      <ng-template #labelTpl>
                        <span class="mb-1 block text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.ORGANIZATION" | translate }}</span>
                      </ng-template>

                      <ng-template #contentTpl>
                        @if (contact.organizationTitle) {
                          <a [routerLink]="organizationRoute" class="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
                            {{ contact.organizationTitle }}
                          </a>
                        } @else {
                          <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50">—</p>
                        }
                      </ng-template>

                      <ng-template #inputTpl>
                        <app-search
                          [formControl]="contactForm.form.controls.organizationId"
                          [items]="(companyOrganizations$ | async)!"
                          [filterFn]="organizationSearchConfig.filterFn"
                          [formatter]="organizationSearchConfig.formatter"
                          [compareFn]="organizationSearchConfig.compareFn"
                          [placeholderText]="'INPUTS.ORGANIZATION' | translate"
                          [errorMessage]="'ERRORS.INCORRECT_SELECTED_ORGANIZATION' | translate"
                          customClass="form-search-input"
                        >
                        </app-search>
                      </ng-template>

                      @if (canCreateOrganization$ | async) {
                        <ng-template #extraTpl let-cancel="cancel">
                          <button
                            type="button"
                            class="mt-3 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                            (click)="cancel(); openCreateOrganizationModal()"
                          >
                            + {{ "BUTTONS.ADD_ORGANIZATION" | translate }}
                          </button>
                        </ng-template>
                      }
                    </app-dynamic-editable-field>

                    <div>
                      <p class="mb-1 text-xs text-neutral-400 dark:text-neutral-500">{{ "INPUTS.RESPONSIBLE" | translate }}</p>

                      <div class="flex items-center justify-between gap-3">
                        <app-minimal-profile-info
                          [profile]="responsibleUser$ | async"
                          imageWidth="32px"
                          imageHeight="32px"
                          fullNameClass="text-sm font-medium text-neutral-900 dark:text-neutral-50"
                          fontSize="11px"
                          [collapseDetailsOnMobile]="false"
                          [customRole]="'USER_ROLES.RESPONSIBLE' | translate"
                        />

                        @if (canEditContact$ | async) {
                          <button
                            #p="ngbPopover"
                            type="button"
                            class="contact-overview-page__responsible-trigger"
                            [ngbPopover]="editForm"
                            popoverClass="popover-lg project-files-rename-popover contact-overview-responsible-popover"
                            autoClose="outside"
                            [placement]="isMobileViewport ? 'bottom-end' : 'auto'"
                            [container]="isMobileViewport ? '' : 'body'"
                            [attr.aria-label]="'INPUTS.RESPONSIBLE' | translate"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M11.3335 2.00016C11.5086 1.82507 11.7166 1.68617 11.9454 1.5914C12.1741 1.49662 12.4193 1.44775 12.6668 1.44775C12.9144 1.44775 13.1595 1.49662 13.3883 1.5914C13.6171 1.68617 13.825 1.82507 14.0002 2.00016C14.1753 2.17525 14.3142 2.38312 14.4089 2.6119C14.5037 2.84069 14.5526 3.0859 14.5526 3.3335C14.5526 3.58109 14.5037 3.8263 14.4089 4.05509C14.3142 4.28387 14.1753 4.49174 14.0002 4.66683L5.3335 13.3335L2.00016 14.0002L2.66683 10.6668L11.3335 2.00016Z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                          </button>

                          <ng-template #editForm>
                            <div class="files-rename-form contact-overview-page__popover-form">
                              <app-search
                                [formControlName]="contactForm.fieldNames.responsibleUserId"
                                [placeholderText]="'INPUTS.RESPONSIBLE' | translate"
                                [items]="(companyEmployees$ | async)!"
                                [filterFn]="employeeFullNameFilter"
                                [formatter]="employeeFormatter"
                                [compareFn]="employeeCompareFn"
                                [errorMessage]="'ERRORS.INCORRECT_SELECTED_RESPONSIBLE' | translate"
                                [resultTemplate]="customTemplate"
                                customClass="files-rename-input input-sm"
                              >
                                <ng-template #customTemplate let-r="result" let-t="term">
                                  <ngb-highlight highlightClass="hover-highlight" [result]="r?.fullName" [term]="t"></ngb-highlight>
                                  <div>{{ r?.email }}</div>
                                </ng-template>
                              </app-search>

                              <button
                                type="button"
                                class="files-rename-action files-rename-action--confirm"
                                [disabled]="contactForm.form.controls.responsibleUserId.invalid"
                                (click)="updateResponsibleUser(contactForm.fieldNames.responsibleUserId, p)"
                              >
                                {{ "BUTTONS.SAVE" | translate }}
                              </button>
                            </div>
                          </ng-template>
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <div class="contact-overview-page__card p-6">
                  <h2 class="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.SECTION_TITLES.INFORMATION" | translate }}
                  </h2>

                  <div class="space-y-3">
                    <div>
                      <p class="mb-1 text-xs text-neutral-400 dark:text-neutral-500">{{ "COMPONENTS.CONTACT_OVERVIEW.INFORMATION.NUMBER" | translate }}</p>
                      <p class="font-mono text-sm font-medium text-neutral-900 dark:text-neutral-50">{{ (contactIdentifier$ | async) || ("COMMON.EMPTY_VALUE" | translate) }}</p>
                    </div>

                    <div>
                      <p class="mb-1 text-xs text-neutral-400 dark:text-neutral-500">{{ "COMPONENTS.CONTACT_OVERVIEW.INFORMATION.CREATED_AT" | translate }}</p>
                      <p class="text-sm text-neutral-700 dark:text-neutral-300">
                        {{ (contactMetadata$ | async)?.createdAt ? ((contactMetadata$ | async)?.createdAt | date: activityDateFormat) : ("COMMON.EMPTY_VALUE" | translate) }}
                      </p>
                    </div>

                    <div>
                      <p class="mb-1 text-xs text-neutral-400 dark:text-neutral-500">{{ "COMPONENTS.CONTACT_OVERVIEW.INFORMATION.UPDATED_AT" | translate }}</p>
                      <p class="text-sm text-neutral-700 dark:text-neutral-300">
                        {{ (contactMetadata$ | async)?.updatedAt ? ((contactMetadata$ | async)?.updatedAt | date: activityDateFormat) : ("COMMON.EMPTY_VALUE" | translate) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          }

          @case ("activity") {
            <div class="contact-overview-page__card p-6">
              <h2 class="mb-5 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                {{ "COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.TITLE" | translate }}
              </h2>

              @if (isActivityLoading) {
                <div class="text-sm text-neutral-500 dark:text-neutral-400">{{ "MESSAGES.LOADING" | translate }}</div>
              } @else if ((activityGroups$ | async)?.length) {
                <div class="contact-overview-page__history-list">
                  @for (group of (activityGroups$ | async) ?? []; track group.dayLabel) {
                    <section class="contact-overview-page__history-group">
                      <p class="contact-overview-page__history-day">
                        {{ group.dayLabel }}
                      </p>

                      <div class="contact-overview-page__history-items">
                        @for (item of group.items; track item.id) {
                          <article class="contact-overview-page__history-item">
                            <div class="contact-overview-page__history-marker" aria-hidden="true">
                              <span class="contact-overview-page__timeline-dot" [ngClass]="item.dotClass"></span>
                            </div>

                            <div class="contact-overview-page__history-head">
                              <div>
                                <p class="contact-overview-page__history-title">{{ item.title }}</p>
                                <p class="contact-overview-page__history-person">
                                  {{ item.createdBy || ("COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.SYSTEM" | translate) }}
                                </p>
                              </div>

                              <time class="contact-overview-page__history-date">{{ item.timeLabel }}</time>
                            </div>

                            @if (item.description) {
                              <div class="contact-overview-page__history-body">
                                <p class="m-0">{{ item.description }}</p>
                              </div>
                            }
                          </article>
                        }
                      </div>
                    </section>
                  }
                </div>
              } @else {
                <div class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100 text-neutral-400 dark:bg-neutral-800">
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                    </svg>
                  </div>
                  <p class="text-sm text-neutral-500 dark:text-neutral-400">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.EMPTY" | translate }}
                  </p>
                </div>
              }
            </div>
          }

          @case ("notes") {
            <div class="space-y-4">
              <div class="contact-overview-page__card p-6">
                <h2 class="mb-3 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                  {{ "COMPONENTS.CONTACT_OVERVIEW.NOTES.NEW_TITLE" | translate }}
                </h2>

                <div>
                  <textarea
                    rows="3"
                    class="form-input form-textarea"
                    [formControl]="newNoteControl"
                    [placeholder]="'COMPONENTS.CONTACT_OVERVIEW.NOTES.PLACEHOLDER' | translate"
                  ></textarea>

                  <div class="mt-3 flex justify-end">
                    <button
                      type="button"
                      class="form-primary-btn form-primary-btn--small"
                      [disabled]="isSubmittingNote"
                      (click)="createNote()"
                    >
                      {{ "COMPONENTS.CONTACT_OVERVIEW.NOTES.ADD_BUTTON" | translate }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="contact-overview-page__card p-6">
                <h2 class="mb-4 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                  {{ "COMPONENTS.CONTACT_OVERVIEW.NOTES.LIST_TITLE" | translate }}
                </h2>

                @if (isNotesLoading) {
                  <div class="text-sm text-neutral-500 dark:text-neutral-400">{{ "MESSAGES.LOADING" | translate }}</div>
                } @else if ((contactNotes$ | async)?.length) {
                  <div class="contact-overview-page__notes-list">
                    @for (note of (contactNotes$ | async) ?? []; track note.id) {
                      <article class="contact-overview-page__note-item">
                        <p class="contact-overview-page__note-text">{{ note.text }}</p>
                        <p class="contact-overview-page__note-meta">
                          {{ note.createdBy || ("COMPONENTS.CONTACT_OVERVIEW.ACTIVITY.SYSTEM" | translate) }} · {{ note.createdAt | date: "medium" }}
                        </p>
                      </article>
                    }
                  </div>
                } @else {
                  <div class="text-sm italic text-neutral-400 dark:text-neutral-500">
                    {{ "COMPONENTS.CONTACT_OVERVIEW.NOTES.EMPTY" | translate }}
                  </div>
                }
              </div>
            </div>
          }

          @case ("files") {
            <div class="contact-overview-page__card p-6">
              <app-attachments-browser
                [attachmentsPath]="contactAttachmentsPath"
                [uploadPath]="contactAttachmentsPath"
                [isLoading]="isFilesLoading"
                [isReadonly]="(canEditContact$ | async) === false"
                [canUpload]="(canUploadFiles$ | async) === true"
                [showBreadcrumbs]="false"
                [allowFolders]="false"
                [showStorageCard]="true"
                [storageUsage]="storageUsage$ | async"
                [files]="(contactFiles$ | async) ?? null"
                [emptyFileListParam]="emptyFileListParam"
                (uploadedFile)="onUploadContactFile($event)"
                (deletedUploadedFile)="onDeleteUploadedContactFile()"
                (reachedStorageLimit)="onReachedStorageLimit()"
                (fileRemove)="deleteContactFileById($event)"
                (fileAction)="handleContactFileAction($event.file, $event.action)"
              ></app-attachments-browser>
            </div>
          }
        }
      }

      <app-reusable-modal
        [isOpen]="isCreateOrganizationModalOpen"
        [panelClass]="'max-w-4xl'"
        [contentScoped]="true"
        [title]="'TITLES.ADD_ORGANIZATION' | translate"
        [subtitle]="'COMPONENTS.ORGANIZATIONS.CREATE_MODAL_SUBTITLE' | translate"
        (closed)="closeCreateOrganizationModal()"
      >
        <app-create-organization
          [companyId]="companyId"
          (cancelled)="closeCreateOrganizationModal()"
          (created)="onOrganizationCreated($event)"
        />
      </app-reusable-modal>

      <app-toasts></app-toasts>
      <ng-template #dangerTpl>
        {{ callbackError }}
      </ng-template>
    </div>
  </div>
}
